/**
 * Hook quản lý danh sách liên hệ (contacts) trong khu admin.
 *
 * Đồng bộ filter với URL, debounce tìm kiếm, phân trang và cập nhật trạng thái liên hệ.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { contactApi } from '../../../contacts/api/contactApi';
import { buildSearchParamsFromAdminFilters } from '../../../../shared/utils/adminFilterSearchParams';

const defaultFilters = {
  search: '',
  status: '',
  page: '1',
};

const buildFiltersFromSearchParams = (searchParams) => ({
  ...defaultFilters,
  search: searchParams.get('search') || '',
  status: searchParams.get('status') || '',
  page: searchParams.get('page') || '1',
});

const removeEmptyFilters = (filters) =>
  Object.fromEntries(
    Object.entries(filters).filter(([key, value]) => {
      if (!value) {
        return false;
      }

      return key !== 'page' || value !== '1';
    })
  );

/**
 * Hook bảng liên hệ admin: lọc, tìm kiếm, phân trang, đổi trạng thái.
 *
 * @returns {Object} contactList, pagination, filters, handlers
 */
export const useAdminContacts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const [contactList, setContactList] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState(() => buildFiltersFromSearchParams(searchParams));
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const [isLoadingContacts, setIsLoadingContacts] = useState(true);
  const [updatingContactId, setUpdatingContactId] = useState(null);

  // URL thay đổi: đồng bộ filter và ô search
  useEffect(() => {
    const urlFilters = buildFiltersFromSearchParams(searchParams);
    setFilters(urlFilters);
    setSearchInput(urlFilters.search);
  }, [searchParamsKey, searchParams]);

  // Filter thay đổi: cập nhật URL nếu khác hiện tại
  useEffect(() => {
    const nextParams = buildSearchParamsFromAdminFilters(filters);
    const nextParamsKey = nextParams.toString();

    if (nextParamsKey !== searchParamsKey) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [filters, searchParamsKey, setSearchParams]);

  const activeFilters = useMemo(() => removeEmptyFilters(filters), [filters]);

  const loadContactList = useCallback(() => {
    setIsLoadingContacts(true);

    return contactApi
      .getContacts(activeFilters)
      .then((response) => {
        setContactList(response.data.data || []);
        setPagination(response.data.pagination || null);
      })
      .finally(() => setIsLoadingContacts(false));
  }, [activeFilters]);

  // Debounce search input 300ms trước khi merge vào filters
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((current) => {
        if (current.search === searchInput.trim()) {
          return current;
        }

        return {
          ...current,
          search: searchInput.trim(),
          page: '1',
        };
      });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadContactList();
  }, [loadContactList]);

  const updateFilter = (field, value) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
      page: '1',
    }));
  };

  const changePage = (page) => {
    setFilters((current) => ({
      ...current,
      page: String(page),
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateContactStatus = async (contactId, status) => {
    setUpdatingContactId(contactId);

    try {
      await contactApi.updateContact(contactId, { status });
      await loadContactList();
    } finally {
      setUpdatingContactId(null);
    }
  };

  const hasActiveFilters = Boolean(searchInput.trim() || filters.status);

  return {
    contactList,
    pagination,
    filters,
    searchInput,
    setSearchInput,
    isLoadingContacts,
    updatingContactId,
    hasActiveFilters,
    updateFilter,
    changePage,
    updateContactStatus,
    reloadContactList: loadContactList,
  };
};
